import { z } from 'zod'

export const idSchema = z
  .string()
  .min(2, 'doit contenir au moins 2 caractères')
  .max(64, 'ne doit pas dépasser 64 caractères')
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'doit utiliser des lettres minuscules, chiffres et tirets')

export const httpUrlSchema = z
  .string()
  .url('doit être une URL valide')
  .refine((value) => {
    try {
      return ['http:', 'https:'].includes(new URL(value).protocol)
    } catch {
      return false
    }
  }, 'seuls les protocoles http et https sont autorisés')

export const httpsUrlSchema = z
  .string()
  .url('doit être une URL valide')
  .refine((value) => {
    try {
      return new URL(value).protocol === 'https:'
    } catch {
      return false
    }
  }, 'seul le protocole https est autorisé')

export const internalPathSchema = z
  .string()
  .max(160)
  .regex(
    /^\/(?:[a-z0-9]+(?:-[a-z0-9]+)*\/)*[a-z0-9]*(?:-[a-z0-9]+)*$/,
    'doit être un chemin interne absolu sans requête ni fragment',
  )
  .refine(
    (value) => value === '/' || !value.endsWith('/'),
    'ne doit pas se terminer par une barre oblique',
  )

export const publicAssetPathSchema = z
  .string()
  .min(1)
  .max(240)
  .refine((value) => {
    return (
      value.startsWith('/') &&
      !value.startsWith('//') &&
      !value.split('/').includes('..') &&
      !value.includes('?') &&
      !value.includes('#')
    )
  }, 'doit être un chemin public local absolu sans requête ni fragment')

export const imageSourceSchema = z
  .string()
  .min(1)
  .refine((value) => {
    if (value.startsWith('/')) return !value.startsWith('//') && !value.split('/').includes('..')
    return httpUrlSchema.safeParse(value).success
  }, 'doit être un chemin public absolu ou une URL http/https')

export const environmentReferenceSchema = z
  .object({
    env: z.string().regex(/^[A-Z_][A-Z0-9_]*$/),
  })
  .strict()

export const LUCIDE_ICON_NAMES = [
  'activity',
  'book-open',
  'boxes',
  'briefcase-business',
  'workflow',
  'calendar-days',
  'chart-no-axes-column-increasing',
  'circle-help',
  'clock-3',
  'cloud',
  'database',
  'external-link',
  'file-signature',
  'file-text',
  'folder-kanban',
  'headphones',
  'house',
  'key-round',
  'layout-dashboard',
  'life-buoy',
  'list-checks',
  'mail',
  'message-square',
  'package-search',
  'settings',
  'shield-check',
  'users-round',
  'wrench',
] as const

export const lucideIconNameSchema = z.enum(LUCIDE_ICON_NAMES)

export const iconSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('image'), value: imageSourceSchema }).strict(),
  z.object({ type: z.literal('url'), value: httpUrlSchema }).strict(),
  z.object({ type: z.literal('emoji'), value: z.string().min(1).max(12) }).strict(),
  z
    .object({
      type: z.literal('lucide'),
      value: lucideIconNameSchema,
    })
    .strict(),
])

export const linkSchema = z
  .object({
    label: z.string().min(1).max(80),
    url: httpUrlSchema,
    target: z.enum(['_self', '_blank']).default('_blank'),
  })
  .strict()
